/**
 * Client-side image compression helper.
 * Runs before upload to drastically reduce payload over network.
 * Server still runs its own WebP pipeline — this just makes the upload fast.
 *
 * Usage:
 *   import { compressImage } from '@/utils/compressImage';
 *   const compressed = await compressImage(file);
 *   formData.append('image', compressed);
 */
import imageCompression from 'browser-image-compression';

const DEFAULTS = {
  maxSizeMB: 1.2,          // target size cap
  maxWidthOrHeight: 1600,  // resize downscale threshold
  useWebWorker: true,      // keep UI responsive
  initialQuality: 0.82,
  fileType: 'image/webp',  // convert to WebP client-side
};

const COMPRESSIBLE = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/bmp', 'image/tiff']);

/**
 * Compress an image File before upload.
 * Skips animated GIFs, SVGs, and tiny files (<200KB).
 * Falls back to original File if compression fails.
 */
export async function compressImage(file, overrides = {}) {
  try {
    if (!file || !(file instanceof File)) return file;
    if (!COMPRESSIBLE.has(file.type)) return file;           // skip gif/svg/etc
    if (file.size < 200 * 1024) return file;                 // skip tiny files

    const opts = { ...DEFAULTS, ...overrides };
    const blob = await imageCompression(file, opts);

    // Rename to .webp extension so backend picks up the right mime
    const base = file.name.replace(/\.[^.]+$/, '');
    const newName = opts.fileType === 'image/webp' ? `${base}.webp` : file.name;
    return new File([blob], newName, { type: blob.type, lastModified: Date.now() });
  } catch (err) {
    console.warn('[compressImage] Fallback to original:', err?.message || err);
    return file;
  }
}

/** Compress an array of files in parallel */
export async function compressImages(files, overrides = {}) {
  return Promise.all(Array.from(files).map(f => compressImage(f, overrides)));
}
